import { FONT_FAMILY } from "@excalidraw/common";

import type { ExcalidrawTextElement } from "@excalidraw/element/types";

import { Fonts } from "./Fonts";
import { FontErrorHandler } from "./FontErrorHandler";

interface FontLoadingState {
  loading: Set<number>;
  loaded: Set<number>;
  failed: Set<number>;
  priority: Map<number, number>; // fontFamily -> priority score
}

interface FontCacheEntry {
  fontFamily: number;
  timestamp: number;
  characters: string;
  fontFaces: FontFace[];
}

interface FontLoadingOptions {
  prioritizeSceneFonts?: boolean;
  maxConcurrency?: number;
  enableCaching?: boolean;
}

/**
 * Enhanced font loading manager that provides:
 * - Parallel font loading without UI blocking
 * - Scene font prioritization logic
 * - Font cache persistence across sessions
 */
export class FontLoadingManager {
  private static instance: FontLoadingManager | null = null;
  private state: FontLoadingState;
  private cache: Map<string, FontCacheEntry>;
  private readonly CACHE_KEY = "excalidraw-font-cache";
  private readonly CACHE_EXPIRY_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

  private constructor() {
    this.state = {
      loading: new Set(),
      loaded: new Set(),
      failed: new Set(),
      priority: new Map(),
    };
    this.cache = new Map();
    this.loadCacheFromStorage();
  }

  public static getInstance(): FontLoadingManager {
    if (!FontLoadingManager.instance) {
      FontLoadingManager.instance = new FontLoadingManager();
    }
    return FontLoadingManager.instance;
  }

  /**
   * Load fonts with enhanced prioritization and caching
   */
  public async loadFonts(
    fontFamilies: Array<ExcalidrawTextElement["fontFamily"]>,
    charsPerFamily: Record<number, Set<string>>,
    options: FontLoadingOptions = {},
  ): Promise<FontFace[]> {
    const {
      prioritizeSceneFonts = true,
      maxConcurrency = 10,
      enableCaching = true,
    } = options;

    // Set priorities for scene fonts
    if (prioritizeSceneFonts) {
      this.setPriorities(fontFamilies);
    }

    // Sort fonts by priority (higher priority first)
    const sortedFamilies = [...fontFamilies].sort((a, b) => {
      const priorityA = this.state.priority.get(a) || 0;
      const priorityB = this.state.priority.get(b) || 0;
      return priorityB - priorityA;
    });

    // Load fonts in parallel with controlled concurrency
    const loadPromises: Promise<FontFace[]>[] = [];
    const semaphore = new Semaphore(maxConcurrency);

    for (const fontFamily of sortedFamilies) {
      const characters = Array.from(charsPerFamily[fontFamily] || []).join("");

      loadPromises.push(
        semaphore.acquire().then(async (release) => {
          try {
            return await this.loadSingleFont(
              fontFamily,
              characters,
              enableCaching,
            );
          } finally {
            release();
          }
        }),
      );
    }

    const results = await Promise.allSettled(loadPromises);
    const loadedFonts: FontFace[] = [];

    results.forEach((result, index) => {
      const fontFamily = sortedFamilies[index];

      if (result.status === "fulfilled") {
        this.state.loaded.add(fontFamily);
        this.state.loading.delete(fontFamily);
        loadedFonts.push(...result.value);
      } else {
        this.state.failed.add(fontFamily);
        this.state.loading.delete(fontFamily);

        // Handle error with fallback - but don't count fallback as additional processing
        const errorHandler = FontErrorHandler.getInstance();
        const fallbackFont = errorHandler.handleFontLoadingError(
          fontFamily,
          result.reason instanceof Error
            ? result.reason
            : new Error(String(result.reason)),
          "loading",
        );

        // Only try to load fallback font if it's not the same as the failed font
        // and if it's not already loaded or being processed
        if (
          fallbackFont !== fontFamily &&
          !this.state.loaded.has(fallbackFont) &&
          !this.state.loading.has(fallbackFont) &&
          !this.state.failed.has(fallbackFont)
        ) {
          const characters = Array.from(charsPerFamily[fontFamily] || []).join(
            "",
          );
          this.loadSingleFont(fallbackFont, characters, enableCaching)
            .then((fallbackFaces) => {
              this.state.loaded.add(fallbackFont);
              loadedFonts.push(...fallbackFaces);
            })
            .catch((fallbackError) => {
              this.state.failed.add(fallbackFont);
              console.error(
                `Fallback font ${fallbackFont} also failed to load:`,
                fallbackError,
              );
            });
        }
      }
    });

    // Persist cache to storage
    if (enableCaching) {
      this.saveCacheToStorage();
    }

    return loadedFonts;
  }

  /**
   * Set priority scores for fonts based on scene usage
   */
  private setPriorities(fontFamilies: Array<number>): void {
    // Clear existing priorities
    this.state.priority.clear();

    // Assign higher priority to fonts used in the scene
    fontFamilies.forEach((fontFamily, index) => {
      // Higher priority for fonts that appear earlier in the list
      // and are used in the current scene
      const basePriority = 100;
      const positionBonus = fontFamilies.length - index;
      this.state.priority.set(fontFamily, basePriority + positionBonus);
    });

    // Give default fonts medium priority
    const defaultFonts = [
      FONT_FAMILY.Virgil,
      FONT_FAMILY.Helvetica,
      FONT_FAMILY.Cascadia,
      FONT_FAMILY.Excalifont,
    ];

    defaultFonts.forEach((fontFamily) => {
      if (!this.state.priority.has(fontFamily)) {
        this.state.priority.set(fontFamily, 50);
      }
    });
  }

  /**
   * Load a single font with caching support
   */
  private async loadSingleFont(
    fontFamily: number,
    characters: string,
    enableCaching: boolean,
  ): Promise<FontFace[]> {
    // Check if already loading
    if (this.state.loading.has(fontFamily)) {
      return [];
    }

    // Check cache first
    if (enableCaching) {
      const cacheKey = `${fontFamily}-${characters}`;
      const cached = this.cache.get(cacheKey);

      if (cached && this.isCacheValid(cached)) {
        return cached.fontFaces;
      }
    }

    this.state.loading.add(fontFamily);

    try {
      // Use existing Fonts loading mechanism
      const registeredFont = Fonts.registered.get(fontFamily);
      if (!registeredFont) {
        throw new Error(`Font family ${fontFamily} not registered`);
      }

      // Add font faces to document if not already added
      if (!registeredFont.metadata.local) {
        for (const { fontFace } of registeredFont.fontFaces) {
          if (!window.document.fonts.has(fontFace)) {
            window.document.fonts.add(fontFace);
          }
        }
      }

      // Load the font
      const fontString = this.getFontString(fontFamily);
      const fontFaces = await window.document.fonts.load(
        fontString,
        characters,
      );

      // Cache the result
      if (enableCaching && fontFaces.length > 0) {
        const cacheKey = `${fontFamily}-${characters}`;
        this.cache.set(cacheKey, {
          fontFamily,
          timestamp: Date.now(),
          characters,
          fontFaces: Array.from(fontFaces),
        });
      }

      return Array.from(fontFaces);
    } catch (error) {
      const errorHandler = FontErrorHandler.getInstance();
      const enhancedError =
        error instanceof Error ? error : new Error(String(error));

      // Handle the error and get fallback
      errorHandler.handleFontLoadingError(fontFamily, enhancedError, "loading");

      console.error(`Failed to load font family ${fontFamily}:`, error);
      throw error;
    }
  }

  /**
   * Get font string for loading
   */
  private getFontString(fontFamily: number): string {
    return `16px ${this.getFontFamilyName(fontFamily)}`;
  }

  /**
   * Get font family name from number
   */
  private getFontFamilyName(fontFamily: number): string {
    const entry = Object.entries(FONT_FAMILY).find(
      ([, value]) => value === fontFamily,
    );
    return entry ? entry[0] : "Excalifont";
  }

  /**
   * Check if cache entry is still valid
   */
  private isCacheValid(entry: FontCacheEntry): boolean {
    return Date.now() - entry.timestamp < this.CACHE_EXPIRY_MS;
  }

  /**
   * Load cache from localStorage
   */
  private loadCacheFromStorage(): void {
    try {
      if (typeof window === "undefined" || !window.localStorage) {
        return;
      }

      const cached = window.localStorage.getItem(this.CACHE_KEY);
      if (!cached) {
        return;
      }

      const data = JSON.parse(cached);
      if (Array.isArray(data)) {
        data.forEach(([key, entry]) => {
          if (this.isCacheValid(entry)) {
            this.cache.set(key, entry);
          }
        });
      }
    } catch (error) {
      console.warn("Failed to load font cache from storage:", error);
      // Clear corrupted cache
      this.clearCache();
    }
  }

  /**
   * Save cache to localStorage
   */
  private saveCacheToStorage(): void {
    try {
      if (typeof window === "undefined" || !window.localStorage) {
        return;
      }

      // Convert Map to array for JSON serialization
      const cacheArray = Array.from(this.cache.entries());
      window.localStorage.setItem(this.CACHE_KEY, JSON.stringify(cacheArray));
    } catch (error) {
      console.warn("Failed to save font cache to storage:", error);
    }
  }

  /**
   * Clear the font cache
   */
  public clearCache(): void {
    this.cache.clear();
    try {
      if (typeof window !== "undefined" && window.localStorage) {
        window.localStorage.removeItem(this.CACHE_KEY);
      }
    } catch (error) {
      console.warn("Failed to clear font cache from storage:", error);
    }
  }

  /**
   * Get loading state for debugging
   */
  public getState(): Readonly<FontLoadingState> {
    return {
      loading: new Set(this.state.loading),
      loaded: new Set(this.state.loaded),
      failed: new Set(this.state.failed),
      priority: new Map(this.state.priority),
    };
  }

  /**
   * Get the font error handler instance
   */
  public getErrorHandler(): FontErrorHandler {
    return FontErrorHandler.getInstance();
  }

  /**
   * Validate and fix font family using error handler
   */
  public validateFontFamily(fontFamily: number): number {
    const errorHandler = FontErrorHandler.getInstance();
    return errorHandler.validateFontFamily(fontFamily);
  }
}

/**
 * Simple semaphore implementation for controlling concurrency
 */
class Semaphore {
  private permits: number;
  private waiting: Array<() => void> = [];

  constructor(permits: number) {
    this.permits = permits;
  }

  async acquire(): Promise<() => void> {
    return new Promise((resolve) => {
      if (this.permits > 0) {
        this.permits--;
        resolve(() => this.release());
      } else {
        this.waiting.push(() => {
          this.permits--;
          resolve(() => this.release());
        });
      }
    });
  }

  private release(): void {
    this.permits++;
    if (this.waiting.length > 0) {
      const next = this.waiting.shift();
      if (next) {
        next();
      }
    }
  }
}
